<template>
  <!-- processing card -->
  <DescriptiveLoader
    v-if="processing"
    text="Power Trip is updating your vehicle's saved settings"
  />
  <!-- save successful card -->
  <v-card flat class="pa-5" v-else-if="saveStatus === 'success'">
    <v-alert type="success" class="mx-2">
      Vehicle updated successfully
    </v-alert>
  </v-card>
  <!-- vehicle edit form -->
  <v-card flat class="pa-5" v-else>
    <v-alert v-if="saveStatus === 'failed'" type="error" class="mx-2 mb-5">
      Something went wrong updating this vehicle. Please try again.
    </v-alert>
    <!-- vehicle card -->
    <v-card class="py-4 pl-md-12 rounded-lg mb-10" flat>
      <v-row class="flex-column flex-md-row">
        <v-col md="3">
          <!-- image section -->
          <v-skeleton-loader
            type="image"
            width="100%"
            height="140px"
            v-if="!vehicle"
          />
          <v-img
            v-else
            contain
            :src="imageSrc()"
            aspect-ratio="1"
            :width="mdAndUp ? '100%' : '40%'"
            :height="mdAndUp ? '100%' : 'auto'"
            style="margin: auto"
          ></v-img>
        </v-col>
        <v-col md="9">
          <!-- details section -->
          <div v-if="vehicle">
            <v-card-title class="pb-0">
              {{ vehicle.licensePlate ?? "unrecorded license plate" }}
            </v-card-title>
            <v-card-subtitle class="font-weight-medium">
              {{ vehicle.name ?? "unnamed vehicle" }}
            </v-card-subtitle>
            <v-card-text v-if="makeModelYearText">
              {{ makeModelYearText }}
            </v-card-text>
          </div>
          <div v-else>
            <v-row no-gutters class="mb-4">
              <v-skeleton-loader type="heading" width="70%" />
            </v-row>
            <v-skeleton-loader type="text" width="40%" />
          </div>
        </v-col>
      </v-row>
    </v-card>
    <!-- settings section -->
    <v-form :disabled="processing || loading">
      <!-- general details -->
      <v-card-title class="pl-0 mb-3"> General details </v-card-title>
      <v-card class="py-4 px-12 rounded-lg mb-10" elevation="1">
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center"> Plate number </v-col>
          <v-col md="8" align-self="center">
            <v-text-field
              v-model="plate"
              bg-color="transparent"
              clearable
              :error-messages="plateError ? [plateError] : []"
              @update:model-value="flagAsDirty"
            />
          </v-col>
        </v-row>
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center"> Vehicle name </v-col>
          <v-col md="8" align-self="center">
            <v-text-field
              v-model="name"
              bg-color="transparent"
              clearable
              :error-messages="nameError ? [nameError] : []"
              @update:model-value="flagAsDirty"
            />
          </v-col>
        </v-row>
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center"> VIN </v-col>
          <v-col md="8" align-self="center">
            <v-text-field
              v-model="vin"
              bg-color="transparent"
              clearable
              @update:model-value="flagAsDirty"
            />
          </v-col>
        </v-row>
        <v-row class="flex-column flex-md-row mb-5">
          <v-col md="4" align-self="center"> Added by </v-col>
          <v-col md="8" align-self="center">
            {{ vehicle?.createdBy ?? "system" }}
          </v-col>
        </v-row>
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center"> Home location </v-col>
          <v-col md="8" align-self="center">
            <v-autocomplete
              v-model="homeLocationExternalId"
              :items="homeLocationOptions"
              item-title="label"
              item-value="value"
              :filter-keys="['title', 'raw.address']"
              clearable
              placeholder="No home location set"
              bg-color="transparent"
              @update:model-value="flagAsDirty"
            />
          </v-col>
        </v-row>
      </v-card>
      <!-- fuel type -->
      <v-card-title class="pl-0 mb-3"> Fuel type </v-card-title>
      <v-card class="py-4 px-12 rounded-lg bg-pwt_snow mb-10" elevation="1">
        <v-row no-gutters>
          <v-col cols="4" v-for="option in fuelTypeOptions" :key="option.value">
            <v-btn
              class="text-caption rounded-lg mr-1 mb-1"
              :class="{ 'btn-border': fuelType !== option.value }"
              flat
              :color="fuelType === option.value ? 'primary20' : undefined"
              @click="
                () => {
                  fuelType = option.value;
                  flagAsDirty();
                }
              "
              style="width: 95%"
            >
              <template v-slot:prepend>
                <v-icon icon="mdi-fuel" color="primary" />
              </template>
              {{ option.label }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
      <!-- weight -->
      <v-card-title class="pl-0 mb-3"> Weight </v-card-title>
      <v-card class="py-4 px-12 rounded-lg bg-pwt_snow mb-10" elevation="1">
        <v-row class="flex-column flex-md-row">
          <v-col md="4" align-self="center"> Gross vehicle mass </v-col>
          <v-col md="8" align-self="center">
            <v-text-field
              v-model="gvm"
              type="number"
              hide-spin-buttons
              suffix="kg"
              :error-messages="gvmError ? [gvmError] : []"
              @update:model-value="flagAsDirty"
              bg-color="transparent"
            />
          </v-col>
        </v-row>
      </v-card>
    </v-form>
    <!-- actions section -->
    <v-card-actions>
      <v-spacer></v-spacer>
      <CustomOutlinedButton @click="toVehicleDetails">
        Cancel
      </CustomOutlinedButton>
      <CustomSolidButton :disabled="!dirty || hasErrors" @click="saveData">
        Save changes
      </CustomSolidButton>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts" setup>
import CustomOutlinedButton from "@/components/ui-elements/buttons/custom-buttons/CustomOutlinedButton.vue";
import CustomSolidButton from "@/components/ui-elements/buttons/custom-buttons/CustomSolidButton.vue";
import DescriptiveLoader from "@/components/ui-elements/loaders/DescriptiveLoader.vue";
import { useFleetStore } from "@/domains/fleet/fleetStore";
import { useSavedLocationStore } from "@/domains/locations/savedLocationStore";
import { useVehicleStore } from "@/domains/vehicles";
import type { FuelType, Vehicle } from "@/domains/vehicles/types";
import { useDashboardNavStore } from "@/stores/dashboardNavStore";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useDisplay } from "vuetify";

const { vehicle } = defineProps<{
  vehicle?: Vehicle;
}>();

const { mdAndUp } = useDisplay();

const dashboardNavStore = useDashboardNavStore();
const vehicleStore = useVehicleStore();
const fleetStore = useFleetStore();
const savedLocationStore = useSavedLocationStore();

function toVehicleDetails(): void {
  dashboardNavStore.displayedContent = "vehicle-details";
}

// control refs
const dirty = ref(false);
const loading = ref(true);
const processing = ref(false);
const saveStatus = ref<"success" | "failed" | undefined>(undefined);

// form fields
const plate = ref<string | null>(null);
const name = ref<string | null>(null);
const vin = ref<string | null>(null);
type VehicleICEFuelType = Extract<FuelType, "PETROL" | "DIESEL" | "HYBRID">;
const fuelType = ref<VehicleICEFuelType>("DIESEL");
const gvm = ref<number | null>(null);
const homeLocationExternalId = ref<string | null>(null);

/**
 * Saved locations that can actually be picked as a home location - filters
 * out anything without an externalId, since only a real, persisted
 * `locations` row can be a valid `home_location_id` FK target.
 */
const homeLocationOptions = computed(() =>
  savedLocationStore.getSavedLocations
    .filter((location) => !!location.externalId)
    .map((location) => ({
      value: location.externalId as string,
      label: location.name || location.address,
      address: location.address,
    })),
);

const fuelTypeOptions: { label: string; value: VehicleICEFuelType }[] = [
  { label: "Petrol", value: "PETROL" },
  { label: "Diesel", value: "DIESEL" },
  { label: "Hybrid", value: "HYBRID" },
];

// Basic pre-submit validation, same spirit as userStore.setAndUpdatePrefs's
// precondition guards - keep obviously-bad writes (a blank identifying
// field, a non-positive weight) from ever reaching the API.
const plateError = computed(() =>
  plate.value?.trim() ? undefined : "Plate number is required",
);
const nameError = computed(() =>
  name.value?.trim() ? undefined : "Vehicle name is required",
);
const gvmError = computed(() =>
  gvm.value != null && gvm.value <= 0
    ? "Gross vehicle mass must be greater than 0"
    : undefined,
);
const hasErrors = computed(
  () => !!(plateError.value || nameError.value || gvmError.value),
);

/**
 * "{make} {model} ({year})", derived from the vehicle's EPA fuel efficiency
 * rating. Undefined when no rating has been resolved/fetched for this
 * vehicle.
 */
const makeModelYearText = computed(() => {
  const rating = vehicle?.externalId
    ? vehicleStore.getEpaFuelEfficiencyRatingForVehicle(vehicle.externalId)
    : undefined;

  return rating
    ? `${rating.make} ${rating.model} (${rating.model_year})`
    : undefined;
});

/**
 * Returns the source URL of the image associated with the vehicle.
 *
 * @return {string} The source URL of the image.
 */
function imageSrc(): string {
  if (vehicle?.imageSrcPaths[0]) return vehicle.imageSrcPaths[0];
  if (fleetStore.logoSrc) return fleetStore.logoSrc;
  return "/images/car_images/placeholder_car.png";
}

/**
 * Snapshot of the values loaded from `vehicle`, so saveData can send only
 * the fields that actually changed rather than resending every field's
 * current value on every save.
 */
let initialValues: {
  plate: string | null;
  name: string | null;
  vin: string | null;
  fuelType: VehicleICEFuelType;
  gvm: number | null;
  homeLocationExternalId: string | null;
} | null = null;

/**
 * Initializes the component's data properties with default values from the vehicle object.
 *
 * @return {void} No return value.
 */
function setInitialValues(): void {
  loading.value = true;
  dirty.value = false;
  if (vehicle) {
    plate.value = vehicle.licensePlate;
    name.value = vehicle.name;
    vin.value = vehicle.VIN;
    fuelType.value =
      vehicle.fuelType === "PETROL" || vehicle.fuelType === "HYBRID"
        ? vehicle.fuelType
        : "DIESEL";
    gvm.value = vehicle.gvm;
    homeLocationExternalId.value = vehicle.homeLocationExternalId;
    initialValues = {
      plate: plate.value,
      name: name.value,
      vin: vin.value,
      fuelType: fuelType.value,
      gvm: gvm.value,
      homeLocationExternalId: homeLocationExternalId.value,
    };
  } else {
    initialValues = null;
  }
  nextTick(() => {
    loading.value = false;
  });
}

/**
 * Flags the component's data as dirty, indicating that it has been modified.
 *
 * @return {void} No return value.
 */
function flagAsDirty(): void {
  if (loading.value) return;
  dirty.value = true;
}

/**
 * Saves the vehicle's edited details. Sets `saveStatus` to "success" or
 * "failed" based on whether the write actually persisted, rather than
 * assuming success once the call returns.
 */
async function saveData(): Promise<void> {
  if (!vehicle?.externalId || hasErrors.value) return;

  const changes: Partial<
    Pick<
      Vehicle,
      | "name"
      | "licensePlate"
      | "VIN"
      | "fuelType"
      | "gvm"
      | "homeLocationExternalId"
    >
  > = {};
  if (plate.value !== initialValues?.plate) changes.licensePlate = plate.value;
  if (name.value !== initialValues?.name) changes.name = name.value;
  if (vin.value !== initialValues?.vin) changes.VIN = vin.value;
  if (fuelType.value !== initialValues?.fuelType)
    changes.fuelType = fuelType.value;
  if (gvm.value !== initialValues?.gvm) changes.gvm = gvm.value;
  if (homeLocationExternalId.value !== initialValues?.homeLocationExternalId)
    changes.homeLocationExternalId = homeLocationExternalId.value;

  if (Object.keys(changes).length === 0) {
    dirty.value = false;
    return;
  }

  processing.value = true;
  saveStatus.value = undefined;

  const success = await vehicleStore.updateVehicle(
    vehicle.externalId,
    changes,
  );

  processing.value = false;

  if (!success) {
    saveStatus.value = "failed";
    return;
  }

  dirty.value = false;
  saveStatus.value = "success";
  setTimeout(() => {
    saveStatus.value = undefined;
  }, 2500);
}

onMounted(async () => {
  await nextTick();
  setInitialValues();
});

watch(
  () => vehicle,
  () => {
    setInitialValues();
  },
);
</script>

<style scoped>
.btn-border {
  border: 0.5px solid #aeaeae;
}
</style>
